import React from 'react';
import { motion } from 'framer-motion';
import { animationConfig } from '../../lib/animations';

// Base skeleton component with app-specific colors and shimmer effect
const SkeletonBase = ({ className = '', children, ...props }) => {
  return (
    <motion.div
      className={`animate-pulse ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={animationConfig.transitions.fast}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Skeleton for text lines with app colors
export const SkeletonText = ({ lines = 1, className = '', ...props }) => {
  return (
    <div className={`space-y-2 ${className}`} {...props}>
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonBase key={index}>
          <div 
            className="h-4 rounded"
            style={{ 
              width: index === lines - 1 ? '75%' : '100%',
              animationDelay: `${index * 0.1}s`,
              backgroundColor: 'rgba(127, 176, 105, 0.2)'
            }}
          />
        </SkeletonBase>
      ))}
    </div>
  );
};

// Skeleton for card components matching app design
export const SkeletonCard = ({ className = '', ...props }) => {
  return (
    <SkeletonBase className={`bg-white rounded-lg shadow-md p-6 ${className}`} {...props}>
      <div className="space-y-4">
        <div className="h-6 rounded w-3/4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
        <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
      </div>
    </SkeletonBase>
  );
};

// Skeleton for chat/message items with app colors
export const SkeletonChatItem = ({ className = '', ...props }) => {
  return (
    <SkeletonBase className={`bg-white rounded-lg shadow-sm p-4 ${className}`} {...props}>
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }} />
        <div className="flex-1 space-y-2">
          <div className="h-4 rounded w-1/4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
          <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
          <div className="h-4 rounded w-3/4" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        </div>
      </div>
    </SkeletonBase>
  );
};

// Skeleton for conversation tiles matching app design
export const SkeletonConversationTile = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`rounded-2xl p-5 mb-4 ${className}`} 
      style={{ 
        backgroundColor: 'rgba(230, 244, 234, 0.6)',
        border: '1px solid rgba(18, 51, 47, 0.1)'
      }}
      {...props}
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="h-5 rounded w-1/3" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
          <div className="h-4 rounded w-1/6" style={{ backgroundColor: 'rgba(142, 142, 147, 0.3)' }} />
        </div>
        <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="flex items-center space-x-2">
          <div className="h-3 rounded w-1/4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.1)' }} />
          <div className="h-3 rounded w-1/6" style={{ backgroundColor: 'rgba(142, 142, 147, 0.2)' }} />
        </div>
      </div>
    </SkeletonBase>
  );
};

// Skeleton for session summary cards with app colors
export const SkeletonSessionCard = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`rounded-2xl shadow-md p-6 ${className}`} 
      style={{ 
        background: 'linear-gradient(135deg, rgba(242, 249, 243, 0.8) 0%, rgba(232, 245, 234, 0.8) 100%)',
        border: '1px solid rgba(127, 176, 105, 0.2)'
      }}
      {...props}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 rounded w-1/2" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
          <div className="h-5 rounded w-1/6" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }} />
        </div>
        <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="h-4 rounded w-3/4" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="flex space-x-2">
          <div className="h-6 rounded w-16" style={{ backgroundColor: 'rgba(18, 51, 47, 0.1)' }} />
          <div className="h-6 rounded w-20" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        </div>
      </div>
    </SkeletonBase>
  );
};

// Skeleton for insight cards with app design
export const SkeletonInsightCard = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`rounded-2xl shadow-md p-6 ${className}`} 
      style={{ 
        background: 'linear-gradient(135deg, rgba(242, 249, 243, 0.8) 0%, rgba(232, 245, 234, 0.8) 100%)',
        border: '1px solid rgba(127, 176, 105, 0.2)'
      }}
      {...props}
    >
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }} />
          <div className="h-5 rounded w-1/3" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
        </div>
        <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="h-4 rounded w-1/2" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
      </div>
    </SkeletonBase>
  );
};

// Skeleton for dashboard stats matching app design
export const SkeletonStatCard = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`rounded-2xl shadow-md p-6 ${className}`} 
      style={{ 
        background: 'linear-gradient(135deg, rgba(242, 249, 243, 0.8) 0%, rgba(232, 245, 234, 0.8) 100%)',
        border: '1px solid rgba(127, 176, 105, 0.2)'
      }}
      {...props}
    >
      <div className="space-y-3">
        <div className="h-5 rounded w-1/2" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
        <div className="h-8 rounded w-1/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }} />
        <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
      </div>
    </SkeletonBase>
  );
};

// Skeleton for form inputs with app colors
export const SkeletonInput = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`h-12 rounded-lg ${className}`} 
      style={{ backgroundColor: 'rgba(127, 176, 105, 0.1)' }}
      {...props} 
    />
  );
};

// Skeleton for buttons with app colors
export const SkeletonButton = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`h-10 rounded-lg w-24 ${className}`} 
      style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }}
      {...props} 
    />
  );
};

// Skeleton for avatar/profile images with app colors
export const SkeletonAvatar = ({ size = 'md', className = '', ...props }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  return (
    <SkeletonBase 
      className={`${sizeClasses[size]} rounded-full ${className}`} 
      style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }}
      {...props} 
    />
  );
};

// Skeleton for list items with staggered animation and app colors
export const SkeletonList = ({ items = 5, className = '', ...props }) => {
  return (
    <div className={`space-y-3 ${className}`} {...props}>
      {Array.from({ length: items }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            duration: animationConfig.duration.fast,
            delay: index * 0.1,
            ease: animationConfig.easing.material
          }}
        >
          <div 
            className="h-16 rounded-2xl" 
            style={{ 
              backgroundColor: 'rgba(230, 244, 234, 0.6)',
              border: '1px solid rgba(18, 51, 47, 0.1)'
            }}
          />
        </motion.div>
      ))}
    </div>
  );
};

// Skeleton for grid layouts with app colors
export const SkeletonGrid = ({ 
  rows = 3, 
  cols = 3, 
  className = '', 
  itemClassName = '',
  ...props 
}) => {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4 ${className}`} {...props}>
      {Array.from({ length: rows * cols }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: animationConfig.duration.normal,
            delay: index * 0.05,
            ease: animationConfig.easing.material
          }}
        >
          <div 
            className={`h-32 rounded-2xl ${itemClassName}`} 
            style={{ 
              backgroundColor: 'rgba(230, 244, 234, 0.6)',
              border: '1px solid rgba(18, 51, 47, 0.1)'
            }}
          />
        </motion.div>
      ))}
    </div>
  );
};

// Skeleton for table rows with app colors
export const SkeletonTableRow = ({ columns = 4, className = '', ...props }) => {
  return (
    <SkeletonBase className={`flex space-x-4 p-3 ${className}`} {...props}>
      {Array.from({ length: columns }).map((_, index) => (
        <div 
          key={index}
          className="h-4 rounded flex-1"
          style={{ 
            width: index === 0 ? '20%' : index === columns - 1 ? '15%' : '25%',
            backgroundColor: 'rgba(127, 176, 105, 0.15)'
          }}
        />
      ))}
    </SkeletonBase>
  );
};

// Skeleton for modal content with app colors
export const SkeletonModal = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`bg-white rounded-2xl p-6 max-w-md mx-auto ${className}`} 
      {...props}
    >
      <div className="space-y-4">
        <div className="h-6 rounded w-3/4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
        <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        <div className="flex space-x-3 pt-2">
          <div className="h-10 rounded w-20" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
          <div className="h-10 rounded w-20" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }} />
        </div>
      </div>
    </SkeletonBase>
  );
};

// Skeleton for navigation/menu items with app colors
export const SkeletonNavItem = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`h-12 rounded-lg ${className}`} 
      style={{ backgroundColor: 'rgba(127, 176, 105, 0.1)' }}
      {...props} 
    />
  );
};

// Skeleton for search bars with app colors
export const SkeletonSearchBar = ({ className = '', ...props }) => {
  return (
    <SkeletonBase 
      className={`h-12 rounded-full ${className}`} 
      style={{ backgroundColor: 'rgba(127, 176, 105, 0.1)' }}
      {...props} 
    />
  );
};

// Skeleton for pagination with app colors
export const SkeletonPagination = ({ className = '', ...props }) => {
  return (
    <SkeletonBase className={`flex space-x-2 ${className}`} {...props}>
      {Array.from({ length: 5 }).map((_, index) => (
        <div 
          key={index} 
          className="w-10 h-10 rounded" 
          style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }}
        />
      ))}
    </SkeletonBase>
  );
};

// Skeleton for loading entire pages with app design
export const SkeletonPage = ({ className = '', ...props }) => {
  return (
    <div 
      className={`min-h-screen p-8 ${className}`} 
      style={{ 
        background: 'linear-gradient(135deg, #f9fff9 0%, #e6f4ea 50%, #a6cdb1 100%)'
      }}
      {...props}
    >
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header skeleton */}
        <div className="space-y-4">
          <div className="h-12 rounded w-1/3" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
          <div className="h-6 rounded w-1/2" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
        </div>
        
        {/* Content skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      </div>
    </div>
  );
};
